<script setup lang="ts">
/**
 * KeyInspectorToggleButton
 *
 * Standalone header icon button that toggles the in-context translation key
 * inspector. Previously a menu item inside AppUserMenu's dropdown (two
 * clicks: open the menu, then click the item) — moved into the header
 * itself, between NotificationBell and AppUserMenu, as a direct one-click
 * toggle alongside the app's other header-level actions.
 *
 * Same access rule as the Localization page — see useKeyInspector's
 * `isAllowed`. Hidden entirely rather than disabled when not entitled, so
 * the header doesn't advertise a feature most roles can't use.
 *
 * Active state is an amber icon colour (matching the inspector's own amber
 * pill/hover-icon elsewhere) rather than an icon swap — unlike the old menu
 * item's pi-key/pi-check-square — since this button's icon stays on screen
 * at all times (not revealed only inside an opened menu), so the on/off
 * state needs to read at a glance without hovering or opening anything.
 * Inactive, it matches NotificationBell's own icon colour (text-gray-600)
 * so the two sit at the same visual weight in the header.
 *
 * `color` is passed directly to <Icon> rather than relying on the button's
 * own text-* class: Icon's `color` prop defaults to 'primary' and applies
 * its own class to the icon's wrapper, which wins over any text colour
 * inherited from an ancestor — setting color only on the <button> here would
 * have had no visible effect on the icon at all.
 */
import { useTranslation } from 'i18next-vue';
import { Icon } from '@tms-phoenix/ui-vue';
import { useKeyInspector } from '@/shared/composables/useKeyInspector';

const { t } = useTranslation();
const { isAllowed, isActive, toggle } = useKeyInspector();
</script>

<template>
  <button
    v-if="isAllowed"
    type="button"
    class="relative flex items-center rounded-lg p-2 transition-colors"
    :class="isActive ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'text-gray-600 hover:bg-gray-100'"
    :aria-label="t('keyInspector.menuLabel', { defaultValue: 'Inspect Translations' })"
    :aria-pressed="isActive"
    :title="t('keyInspector.menuLabel', { defaultValue: 'Inspect Translations' })"
    data-testid="key-inspector-toggle-button"
    @click="toggle"
  >
    <Icon
      name="Translate"
      size-class="h-6 w-6"
      :color="isActive ? 'text-amber-600' : 'text-gray-600'"
      :decorative="true"
    />
  </button>
</template>
